import { Injectable, OnDestroy } from '@angular/core';
import { Observable, Subject, BehaviorSubject, timer } from 'rxjs';
import { map, filter, takeUntil, switchMap } from 'rxjs/operators';
import { NGXLogger } from 'ngx-logger';
import { GooglePubSubService } from './google-pubsub.service';
import {
  ApplicationMessage,
  MessageType,
  FascicoloMessage,
  WizardMessage,
  FileMessage,
  NotificationMessage,
  SystemAlertMessage,
  PubSubMessageData
} from '../../models/pubsub/pubsub-message.model';
import { DomandaSostegnoPubblicataMessage } from '../../models/pubsub/domanda-sostegno-pubblicata.model';

@Injectable({
  providedIn: 'root'
})
export class AgriFlowMessagingService implements OnDestroy {
  private destroy$ = new Subject<void>();

  // Subject per i diversi tipi di messaggi
  private fascicoloMessages$ = new Subject<FascicoloMessage>();
  private wizardMessages$ = new Subject<WizardMessage>();
  private fileMessages$ = new Subject<FileMessage>();
  private notificationMessages$ = new Subject<NotificationMessage>();
  private systemAlertMessages$ = new Subject<SystemAlertMessage>();

  // Statistiche
  private messageStats = {
    totalReceived: 0,
    fascicoloMessages: 0,
    wizardMessages: 0,
    fileMessages: 0,
    notificationMessages: 0,
    systemAlertMessages: 0,
    lastMessageTime: new Date()
  };

  constructor(
    private pubsubService: GooglePubSubService,
    private logger: NGXLogger
  ) {
    this.initializeService();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Inizializza il servizio in modo asincrono
   */
  private async initializeService(): Promise<void> {
    try {
      // Aspetta che il servizio Pub/Sub sia connesso
      await this.waitForConnection();

      // Inizializza i gestori dei messaggi
      this.initializeMessageHandlers();

      // Avvia il controllo di salute
      this.startHealthCheck();

     /* this.logger.info('[AGRIFLOW-MESSAGING] Servizio inizializzato con successo'); */
    } catch (error) {
      this.logger.error('[AGRIFLOW-MESSAGING] Errore inizializzazione servizio:', error);
    }
  }

  /**
   * Aspetta che la connessione Pub/Sub sia stabilita
   */
  private async waitForConnection(): Promise<void> {
    const maxAttempts = 10;
    const delayMs = 500;

    for (let i = 0; i < maxAttempts; i++) {
      if (this.pubsubService.isServiceConnected()) {
        return;
      }

      await new Promise(resolve => setTimeout(resolve, delayMs));
    }

    throw new Error('Timeout: Impossibile stabilire connessione Pub/Sub');
  }

  /**
   * Inizializza i gestori dei messaggi
   */
  private initializeMessageHandlers(): void {
    // Gestisce tutti i messaggi ricevuti
    this.pubsubService.getMessages()
      .pipe(takeUntil(this.destroy$))
      .subscribe(message => {
        this.handleIncomingMessage(message);
      });

   // this.logger.info('[AGRIFLOW-MESSAGING] Gestori messaggi inizializzati');
  }

  /**
   * Gestisce i messaggi in arrivo
   */
  private handleIncomingMessage(message: PubSubMessageData): void {
    try {
      this.messageStats.totalReceived++;
      this.messageStats.lastMessageTime = new Date();

      const messageData = message.data as ApplicationMessage;

      switch (messageData.type) {
        case MessageType.FASCICOLO_CREATED:
        case MessageType.FASCICOLO_UPDATED:
        case MessageType.FASCICOLO_DELETED:
          this.fascicoloMessages$.next(messageData as FascicoloMessage);
          this.messageStats.fascicoloMessages++;
          break;

        case MessageType.WIZARD_STARTED:
        case MessageType.WIZARD_COMPLETED:
        case MessageType.WIZARD_FAILED:
          this.wizardMessages$.next(messageData as WizardMessage);
          this.messageStats.wizardMessages++;
          break;

        case MessageType.FILE_UPLOADED:
        case MessageType.FILE_PROCESSED:
          this.fileMessages$.next(messageData as FileMessage);
          this.messageStats.fileMessages++;
          break;

        case MessageType.DOMANDA_SOSTEGNO_PUBBLICATA:
          this.fileMessages$.next(messageData as any);
          this.messageStats.fileMessages++;
          break;

        case MessageType.NOTIFICATION:
          this.notificationMessages$.next(messageData as NotificationMessage);
          this.messageStats.notificationMessages++;
          break;

        case MessageType.SYSTEM_ALERT:
          this.systemAlertMessages$.next(messageData as SystemAlertMessage);
          this.messageStats.systemAlertMessages++;
          break;

        default:
          this.logger.warn('[AGRIFLOW-MESSAGING] Tipo messaggio sconosciuto:', (messageData as any).type);
      }

    } catch (error) {
      this.logger.error('[AGRIFLOW-MESSAGING] Errore gestione messaggio:', error);
    }
  }

  /**
   * Avvia il controllo di salute del servizio
   */
  private startHealthCheck(): void {
    timer(0, 30000) // Ogni 30 secondi
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.checkServiceHealth();
      });
  }

  /**
   * Controlla la salute del servizio
   */
  private checkServiceHealth(): void {
    const isConnected = this.pubsubService.isServiceConnected();

    if (!isConnected) {
      this.logger.warn('[AGRIFLOW-MESSAGING] Servizio Pub/Sub disconnesso - tentativo di riconnessione...');

      // Tenta di riconnettere invece di pubblicare un alert
      this.pubsubService.reconnect().then(() => {
        this.logger.info('[AGRIFLOW-MESSAGING] Riconnessione Pub/Sub riuscita');
      }).catch(error => {
        this.logger.error('[AGRIFLOW-MESSAGING] Errore riconnessione Pub/Sub:', error);
      });
    }
  }

  // ===== OBSERVABLES PER I MESSAGGI =====

  /**
   * Ottiene i messaggi di fascicolo
   */
  getFascicoloMessages(): Observable<FascicoloMessage> {
    return this.fascicoloMessages$.asObservable();
  }

  /**
   * Ottiene i messaggi di wizard
   */
  getWizardMessages(): Observable<WizardMessage> {
    return this.wizardMessages$.asObservable();
  }

  /**
   * Ottiene i messaggi di file
   */
  getFileMessages(): Observable<FileMessage> {
    return this.fileMessages$.asObservable();
  }

  /**
   * Ottiene le notifiche
   */
  getNotificationMessages(): Observable<NotificationMessage> {
    return this.notificationMessages$.asObservable();
  }

  /**
   * Ottiene gli alert di sistema
   */
  getSystemAlertMessages(): Observable<SystemAlertMessage> {
    return this.systemAlertMessages$.asObservable();
  }

  /**
   * Ottiene tutti i messaggi
   */
  getAllMessages(): Observable<ApplicationMessage> {
    return this.pubsubService.getMessages().pipe(
      map(message => message.data as ApplicationMessage)
    );
  }

  // ===== METODI DI PUBBLICAZIONE =====

  /**
   * Verifica se il servizio è pronto per pubblicare messaggi
   */
  private isServiceReady(): boolean {
    return this.pubsubService.isServiceConnected();
  }

  /**
   * Pubblica un messaggio di fascicolo creato
   */
  async publishFascicoloCreated(
    fascicoloId: string,
    beneficiario: { nome: string; cognome: string; codiceFiscale: string },
    userId?: string,
    metadata?: { [key: string]: any }
  ): Promise<void> {
    if (!this.isServiceReady()) {
      this.logger.warn('[AGRIFLOW-MESSAGING] Servizio non pronto, messaggio fascicolo creato non pubblicato:', fascicoloId);
      return;
    }

    try {
      await this.pubsubService.publishFascicoloMessage(
        MessageType.FASCICOLO_CREATED,
        fascicoloId,
        beneficiario,
        userId,
        metadata
      );
      this.logger.info('[AGRIFLOW-MESSAGING] Messaggio fascicolo creato pubblicato:', fascicoloId);
    } catch (error) {
      this.logger.error('[AGRIFLOW-MESSAGING] Errore pubblicazione fascicolo creato:', error);
    }
  }

  /**
   * Pubblica un messaggio di fascicolo aggiornato
   */
  async publishFascicoloUpdated(
    fascicoloId: string,
    beneficiario: { nome: string; cognome: string; codiceFiscale: string },
    userId?: string,
    metadata?: { [key: string]: any }
  ): Promise<void> {
    if (!this.isServiceReady()) {
      this.logger.warn('[AGRIFLOW-MESSAGING] Servizio non pronto, messaggio fascicolo aggiornato non pubblicato:', fascicoloId);
      return;
    }

    try {
      await this.pubsubService.publishFascicoloMessage(
        MessageType.FASCICOLO_UPDATED,
        fascicoloId,
        beneficiario,
        userId,
        metadata
      );
      this.logger.info('[AGRIFLOW-MESSAGING] Messaggio fascicolo aggiornato pubblicato:', fascicoloId);
    } catch (error) {
      this.logger.error('[AGRIFLOW-MESSAGING] Errore pubblicazione fascicolo aggiornato:', error);
    }
  }

  /**
   * Pubblica un messaggio di wizard completato
   */
  async publishWizardCompleted(
    wizardId: string,
    step: number,
    totalSteps: number,
    fascicoloId?: string,
    userId?: string,
    metadata?: { [key: string]: any }
  ): Promise<void> {
    if (!this.isServiceReady()) {
      this.logger.warn('[AGRIFLOW-MESSAGING] Servizio non pronto, messaggio wizard completato non pubblicato:', wizardId);
      return;
    }

    try {
      await this.pubsubService.publishWizardMessage(
        MessageType.WIZARD_COMPLETED,
        wizardId,
        step,
        totalSteps,
        100, // progress = 100%
        fascicoloId,
        userId,
        undefined,
        metadata
      );
      this.logger.info('[AGRIFLOW-MESSAGING] Messaggio wizard completato pubblicato:', wizardId);
    } catch (error) {
      this.logger.error('[AGRIFLOW-MESSAGING] Errore pubblicazione wizard completato:', error);
    }
  }

  /**
   * Pubblica un messaggio di domanda sostegno pubblicata
   */
  async publishDomandaSostegnoPubblicata(
    domandaSostegnoPubblicataMessage: DomandaSostegnoPubblicataMessage
  ): Promise<void> {
    await this.pubsubService.publishDomandaSostegnoPubblicataMessage(domandaSostegnoPubblicataMessage);
    this.logger.info('[AGRIFLOW-MESSAGING] Messaggio domanda sostegno pubblicata:', domandaSostegnoPubblicataMessage.fascicoloProcessingUID);

  }

  /**
   * Pubblica un messaggio di file caricato
   */
  async publishFileUploaded(
    fileId: string,
    fileName: string,
    fileSize: number,
    fileType: string,
    fascicoloId?: string,
    wizardId?: string,
    userId?: string,
    metadata?: { [key: string]: any }
  ): Promise<void> {
    if (!this.isServiceReady()) {
      this.logger.warn('[AGRIFLOW-MESSAGING] Servizio non pronto, messaggio file caricato non pubblicato:', fileId);
      return;
    }

    try {
      await this.pubsubService.publishFileMessage(
        MessageType.FILE_UPLOADED,
        fileId,
        fileName,
        fileSize,
        fileType,
        fascicoloId,
        wizardId,
        userId,
        undefined,
        metadata
      );
      this.logger.info('[AGRIFLOW-MESSAGING] Messaggio file caricato pubblicato:', fileId);
    } catch (error) {
      this.logger.error('[AGRIFLOW-MESSAGING] Errore pubblicazione file caricato:', error);
    }
  }

  /**
   * Pubblica una notifica
   */
  async publishNotification(
    notificationId: string,
    title: string,
    message: string,
    priority: 'low' | 'medium' | 'high' | 'urgent' = 'medium',
    category: 'info' | 'warning' | 'error' | 'success' = 'info',
    userId?: string,
    expiresAt?: Date,
    metadata?: { [key: string]: any }
  ): Promise<void> {
    if (!this.isServiceReady()) {
      this.logger.warn('[AGRIFLOW-MESSAGING] Servizio non pronto, notifica non pubblicata:', notificationId);
      return;
    }

    try {
      await this.pubsubService.publishNotification(
        notificationId,
        title,
        message,
        priority,
        category,
        userId,
        expiresAt,
        metadata
      );
      this.logger.info('[AGRIFLOW-MESSAGING] Notifica pubblicata:', notificationId);
    } catch (error) {
      this.logger.error('[AGRIFLOW-MESSAGING] Errore pubblicazione notifica:', error);
    }
  }

  /**
   * Pubblica un alert di sistema
   */
  async publishSystemAlert(
    alertId: string,
    severity: 'info' | 'warning' | 'error' | 'critical',
    component: string,
    message: string,
    resolved: boolean = false,
    metadata?: { [key: string]: any }
  ): Promise<void> {
    if (!this.isServiceReady()) {
      this.logger.warn('[AGRIFLOW-MESSAGING] Servizio non pronto, alert di sistema non pubblicato:', alertId);
      return;
    }

    try {
      await this.pubsubService.publishSystemAlert(
        alertId,
        severity,
        component,
        message,
        resolved,
        metadata
      );
      this.logger.info('[AGRIFLOW-MESSAGING] Alert di sistema pubblicato:', alertId);
    } catch (error) {
      this.logger.error('[AGRIFLOW-MESSAGING] Errore pubblicazione alert di sistema:', error);
    }
  }

  // ===== UTILITY =====

  /**
   * Ottiene le statistiche dei messaggi
   */
  getMessageStats() {
    return { ...this.messageStats };
  }

  /**
   * Verifica se il servizio è connesso
   */
  isConnected(): boolean {
    return this.pubsubService.isServiceConnected();
  }

  /**
   * Ottiene lo stato della connessione
   */
  getConnectionStatus(): Observable<boolean> {
    return this.pubsubService.getConnectionStatus();
  }

  /**
   * Riconnette al servizio
   */
  async reconnect(): Promise<void> {
    await this.pubsubService.reconnect();
  }

  /**
   * Filtra i messaggi per fascicolo
   */
  getMessagesForFascicolo(fascicoloId: string): Observable<ApplicationMessage> {
    return this.getAllMessages().pipe(
      filter(message =>
        (message as any).fascicoloId === fascicoloId
      )
    );
  }

  /**
   * Filtra i messaggi per wizard
   */
  getMessagesForWizard(wizardId: string): Observable<ApplicationMessage> {
    return this.getAllMessages().pipe(
      filter(message =>
        (message as any).wizardId === wizardId
      )
    );
  }

  /**
   * Filtra i messaggi per utente
   */
  getMessagesForUser(userId: string): Observable<ApplicationMessage> {
    return this.getAllMessages().pipe(
      filter(message =>
        (message as any).userId === userId
      )
    );
  }
}
